import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
};

const toApiDateTime = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '';
};

const buildDateTime = (workDt, timeValue, nextDay) => {
  const base = dayjs(`${workDt} ${timeValue || '00:00'}`);
  return (nextDay ? base.add(1, 'day') : base).format('YYYY-MM-DDTHH:mm');
};

const WorkScheduleChangeRequestDialog = ({
  open,
  row,
  shifts = [],
  saving,
  onClose,
  onSubmit,
}) => {
  const [shiftId, setShiftId] = useState('');
  const [startDtm, setStartDtm] = useState('');
  const [endDtm, setEndDtm] = useState('');
  const [reason, setReason] = useState('');

  const shiftMap = useMemo(() => {
    const map = {};
    shifts.forEach((shift) => {
      map[shift.shiftId] = shift;
    });
    return map;
  }, [shifts]);

  useEffect(() => {
    if (!open || !row) return;
    setShiftId(row.shiftId || '');
    setStartDtm(toDateTimeLocal(row.startDtm));
    setEndDtm(toDateTimeLocal(row.endDtm));
    setReason('');
  }, [open, row]);

  const handleShiftChange = (nextShiftId) => {
    const shift = shiftMap[nextShiftId];
    setShiftId(nextShiftId);
    if (!row || !shift) return;

    setStartDtm(buildDateTime(row.workDt, shift.startTm, false));
    setEndDtm(buildDateTime(row.workDt, shift.endTm, shift.endNextDayYn === 'Y'));
  };

  const handleSubmit = () => {
    if (!row || !shiftId || !startDtm || !endDtm || !reason.trim()) {
      return;
    }

    onSubmit({
      reqTyp: 'CHANGE',
      reqScheduleId: row.scheduleId,
      reqWorkDt: dayjs(row.workDt).format('YYYY-MM-DD'),
      reqShiftId: shiftId,
      reqStartDtm: toApiDateTime(startDtm),
      reqEndDtm: toApiDateTime(endDtm),
      reason: reason.trim(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>근무표 변경 요청</DialogTitle>
      <DialogContent dividers>
        {row && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">근무일</Typography>
                <Typography variant="body2">{dayjs(row.workDt).format('YYYY-MM-DD')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">현재 근무</Typography>
                <Typography variant="body2">{row.shiftNm || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">현재 시간</Typography>
                <Typography variant="body2">
                  {row.startDtm ? dayjs(row.startDtm).format('YYYY-MM-DD HH:mm') : '-'}
                  {' ~ '}
                  {row.endDtm ? dayjs(row.endDtm).format('YYYY-MM-DD HH:mm') : '-'}
                </Typography>
              </Grid>
            </Grid>

            <TextField
              select
              fullWidth
              size="small"
              label="변경 근무유형"
              value={shiftId}
              onChange={(event) => handleShiftChange(event.target.value)}
            >
              {shifts.map((shift) => (
                <MenuItem key={shift.shiftId} value={shift.shiftId}>
                  {shift.shiftNm}
                </MenuItem>
              ))}
            </TextField>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="datetime-local"
                  fullWidth
                  size="small"
                  label="변경 출근"
                  value={startDtm}
                  onChange={(event) => setStartDtm(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="datetime-local"
                  fullWidth
                  size="small"
                  label="변경 퇴근"
                  value={endDtm}
                  onChange={(event) => setEndDtm(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="변경 사유 *"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              error={!reason.trim()}
              helperText={!reason.trim() ? '변경 사유를 입력하세요.' : ' '}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">닫기</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !row || !shiftId || !startDtm || !endDtm || !reason.trim()}
        >
          요청
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkScheduleChangeRequestDialog;
